import React from 'react';
import {
  Box,
  CardContent,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { 
  MusicNote, 
  Schedule, 
  Build, 
  CloudOff 
} from '@mui/icons-material';
import { GlassCard, WaveformVisualizer } from '../ui';

export const MaintenanceMode: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0A0E1A 0%, #1A1A2E 50%, #16213E 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255, 107, 53, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(142, 36, 170, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <GlassCard variant="elevated" sx={{ maxWidth: 450, width: '100%', position: 'relative', zIndex: 1 }}>
        <CardContent sx={{ p: 5, textAlign: 'center' }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mb: 3
            }}>
              <MusicNote sx={{ fontSize: 40, color: 'primary.main' }} />
              <WaveformVisualizer animated color="#FF6B35" />
            </Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Abydos Studio Hub
            </Typography>
            <Chip
              icon={<CloudOff />}
              label="Temporarily Offline"
              color="warning"
              variant="outlined"
              sx={{
                fontSize: '0.9rem',
                fontWeight: 600,
                borderColor: 'warning.main',
                color: 'warning.main',
                '& .MuiChip-icon': {
                  color: 'warning.main'
                }
              }}
            />
          </Box>

          {/* Maintenance Alert */}
          <Alert
            severity="info"
            icon={<Build />}
            sx={{
              mb: 4,
              background: 'rgba(33, 150, 243, 0.12)',
              border: '1px solid rgba(33, 150, 243, 0.35)',
              textAlign: 'left',
              '& .MuiAlert-icon': {
                color: '#2196F3',
              },
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Scheduled Maintenance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              We're currently performing system maintenance to improve your experience. 
              All DAW collaboration features will be restored shortly.
            </Typography>
          </Alert>

          {/* Status Information */}
          <Box sx={{ 
            background: 'rgba(255, 255, 255, 0.05)', 
            borderRadius: 2, 
            p: 3, 
            mb: 4,
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Schedule sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Maintenance Details
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left' }}>
              <strong>What's being updated:</strong><br />
              • Database optimization<br />
              • Security enhancements<br />
              • Performance improvements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
              <strong>Expected duration:</strong> 30-60 minutes<br />
              <strong>Data safety:</strong> All your projects and sessions are secure
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box sx={{ 
            textAlign: 'center',
            pt: 3, 
            borderTop: '1px solid rgba(255, 255, 255, 0.1)' 
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Need immediate assistance?
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'primary.main', 
              fontWeight: 600,
              '& a': {
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}>
              Contact: <a href="mailto:support@abydosone.ltd">support@abydosone.ltd</a>
            </Typography>
          </Box>

          {/* Auto-refresh notice */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              This page will automatically redirect once maintenance is complete
            </Typography>
          </Box>
        </CardContent>
      </GlassCard>
    </Box>
  );
};